/**
 * The pure budget-allocation engine behind the "Ad Budget & Daily Limits" modal, split out of
 * the component so the weighting + fallback maths are unit-tested without a render. No React,
 * no side effects: same inputs, same outputs.
 */
import { acquisitionAftFor } from '../../../../config/acquisitionChannels';
import type { ChannelFigure } from '../../channel-figures';

export type ChannelMode = 'manual' | 'lumio';

export type Channel = {
  id: string;
  name: string;
  color: string;
  icon: string;
  cpa: number;
  spent: number;
  mode: ChannelMode;
  manualLimit: number;
  lumioReason?: string;
  /** Live in this release? Gated channels (release feature flag) get $0 and a frosted
   *  lock veil; the budget split skips them so the whole pool flows to the live ones.
   *  See `config/acquisitionChannels`. */
  enabled: boolean;
};

export type AllocationResult = {
  alloc: Record<string, number>;
  unallocated: number;
  over: number;
  total: number;
  lumioCount: number;
};

export function computeAllocation(globalLimit: number, channels: Channel[]): AllocationResult {
  // Gated-off channels (release feature flag) take no budget and are excluded from the
  // split, so the entire pool flows to the live channels (Web only, in the first
  // release). They read $0; flip a channel live in `config/acquisitionChannels` and it
  // rejoins the weighted split with no other change here.
  const live = channels.filter((c) => c.enabled);
  const manual = live.filter((c) => c.mode === 'manual');
  const lumio = live.filter((c) => c.mode === 'lumio');
  const manualSum = manual.reduce((s, c) => s + c.manualLimit, 0);
  const pool = globalLimit - manualSum;
  const alloc: Record<string, number> = {};

  channels.filter((c) => !c.enabled).forEach((c) => (alloc[c.id] = 0));
  manual.forEach((c) => (alloc[c.id] = c.manualLimit));

  let unallocated = 0;
  let over = 0;

  if (pool < 0) {
    over = -pool;
    lumio.forEach((c) => (alloc[c.id] = 0));
  } else if (lumio.length === 0) {
    unallocated = pool;
  } else {
    // Lumio funds the better performers harder: weight ∝ 1/CPA. A non-positive CPA (a live
    // channel with no delivery yet, so no real CPA) contributes 0; if EVERY Lumio channel is
    // in that state the split falls back to even, so the pool is never divided by zero.
    const rawWeights = lumio.map((c) => (c.cpa > 0 ? 1 / c.cpa : 0));
    const rawWeightSum = rawWeights.reduce((a, b) => a + b, 0);
    const weights = rawWeightSum > 0 ? rawWeights : lumio.map(() => 1);
    const weightSum = rawWeightSum > 0 ? rawWeightSum : lumio.length;
    let assigned = 0;
    lumio.forEach((c, i) => {
      const v = Math.max(0, Math.round((pool * weights[i]) / weightSum / 5) * 5);
      alloc[c.id] = v;
      assigned += v;
    });
    // absorb rounding drift into the last Lumio channel
    alloc[lumio[lumio.length - 1].id] += pool - assigned;
  }

  const total = channels.reduce((s, c) => s + (alloc[c.id] ?? 0), 0);

  return { alloc, unallocated, over, total, lumioCount: lumio.length };
}

/**
 * Overlay the live per-channel CPA onto the allocation inputs. Each enabled channel with a
 * real CPA (from the acquisition-channel figures, keyed by `aft`) uses it as its Lumio weight;
 * everything else keeps its scaffold CPA so the split never divides by a missing figure.
 */
export function withRealCpa(channels: Channel[], figures: Record<string, ChannelFigure>): Channel[] {
  return channels.map((c) => {
    const aft = acquisitionAftFor(c.id);
    const real = aft ? figures[aft]?.cpaValue : null;
    return real && real > 0 ? { ...c, cpa: real } : c;
  });
}
